import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import {
  FiMail,
  FiUser,
  FiCalendar,
  FiEye,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiDownload,
} from "react-icons/fi";
import { contactsAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import Modal from "../components/Modal";

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedContact, setSelectedContact] = useState(null);
  const [viewModal, setViewModal] = useState({ isOpen: false, contact: null });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    contact: null,
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    let filtered = contacts;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (contact) =>
          contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.message.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((contact) => contact.status === statusFilter);
    }

    setFilteredContacts(filtered);
  }, [searchTerm, statusFilter, contacts]);

  const fetchContacts = async () => {
    try {
      const response = await contactsAPI.getContacts();
      setContacts(response.data);
      setFilteredContacts(response.data);
    } catch (error) {
      toast.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      await contactsAPI.updateContactStatus(contactId, newStatus);
      setContacts(
        contacts.map((contact) =>
          contact._id === contactId
            ? { ...contact, status: newStatus }
            : contact
        )
      );
      toast.success("Contact status updated");
    } catch (error) {
      toast.error("Failed to update contact status");
    }
  };

  const handleDelete = async (contactId) => {
    try {
      await contactsAPI.deleteContact(contactId);
      setContacts(contacts.filter((c) => c._id !== contactId));
      toast.success("Contact deleted successfully");
      setDeleteModal({ isOpen: false, contact: null });
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const handleViewContact = (contact) => {
    setViewModal({ isOpen: true, contact });
    if (contact.status === "new") {
      updateContactStatus(contact._id, "read");
    }
  };

  const exportContacts = () => {
    const csvContent = [
      ["Name", "Email", "Subject", "Message", "Status", "Date"].join(","),
      ...filteredContacts.map((contact) =>
        [
          `"${contact.name}"`,
          `"${contact.email}"`,
          `"${contact.subject}"`,
          `"${contact.message.replace(/"/g, '""')}"`,
          contact.status,
          format(new Date(contact.createdAt), "yyyy-MM-dd HH:mm:ss"),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contacts-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-800",
      read: "bg-yellow-100 text-yellow-800",
      replied: "bg-green-100 text-green-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const stats = {
    total: contacts.length,
    new: contacts.filter((c) => c.status === "new").length,
    read: contacts.filter((c) => c.status === "read").length,
    replied: contacts.filter((c) => c.status === "replied").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
          <p className="text-gray-600">
            Manage and respond to contact form submissions
          </p>
        </div>
        <button onClick={exportContacts} className="btn btn-secondary">
          <FiDownload className="mr-2" size={16} />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: "Total", value: stats.total, color: "blue" },
          { label: "New", value: stats.new, color: "blue" },
          { label: "Read", value: stats.read, color: "yellow" },
          { label: "Replied", value: stats.replied, color: "green" },
        ].map((stat, index) => (
          <div key={index} className="card p-4">
            <div className="flex items-center">
              <div className={`p-3 rounded-full bg-${stat.color}-100`}>
                <FiMail className={`text-${stat.color}-600`} size={20} />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="card">
        {filteredContacts.length === 0 ? (
          <div className="text-center py-12">
            <FiMail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || statusFilter !== "all"
                ? "No contacts found"
                : "No messages yet"}
            </h3>
            <p className="text-gray-500">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search or filter criteria"
                : "Contact messages will appear here when people reach out"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Contact</th>
                  <th className="table-header">Subject</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredContacts.map((contact) => (
                  <tr key={contact._id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <FiUser className="text-gray-400" size={20} />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {contact.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {contact.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {contact.subject}
                      </div>
                    </td>
                    <td className="table-cell">
                      <select
                        value={contact.status}
                        onChange={(e) =>
                          updateContactStatus(contact._id, e.target.value)
                        }
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${getStatusColor(
                          contact.status
                        )}`}
                      >
                        <option value="new">New</option>
                        <option value="read">Read</option>
                        <option value="replied">Replied</option>
                      </select>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">
                        {format(new Date(contact.createdAt), "MMM dd, yyyy")}
                      </div>
                      <div className="text-sm text-gray-500">
                        {format(new Date(contact.createdAt), "HH:mm")}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewContact(contact)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="View message"
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          onClick={() =>
                            setDeleteModal({ isOpen: true, contact })
                          }
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete message"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Contact Modal */}
      <Modal
        isOpen={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, contact: null })}
        title="Contact Message"
        size="lg"
      >
        {viewModal.contact && (
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Name
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {viewModal.contact.name}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Email
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    <a
                      href={`mailto:${viewModal.contact.email}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {viewModal.contact.email}
                    </a>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Date
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {format(new Date(viewModal.contact.createdAt), "PPP pp")}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Status
                  </label>
                  <p className="text-sm mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                        viewModal.contact.status
                      )}`}
                    >
                      {viewModal.contact.status}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="text-sm font-medium text-gray-600">
                Subject
              </label>
              <p className="text-lg font-medium text-gray-900 mt-1">
                {viewModal.contact.subject}
              </p>
            </div>

            {/* Message */}
            <div>
              <label className="text-sm font-medium text-gray-600">
                Message
              </label>
              <div className="mt-1 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-900 whitespace-pre-wrap">
                  {viewModal.contact.message}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="flex space-x-3">
                <a
                  href={`mailto:${viewModal.contact.email}?subject=Re: ${viewModal.contact.subject}&body=Hi ${viewModal.contact.name},%0D%0A%0D%0AThank you for your message.%0D%0A%0D%0A`}
                  className="btn btn-primary"
                  onClick={() =>
                    updateContactStatus(viewModal.contact._id, "replied")
                  }
                >
                  <FiMail className="mr-2" size={16} />
                  Reply via Email
                </a>

                <select
                  value={viewModal.contact.status}
                  onChange={(e) => {
                    updateContactStatus(viewModal.contact._id, e.target.value);
                    setViewModal({
                      ...viewModal,
                      contact: { ...viewModal.contact, status: e.target.value },
                    });
                  }}
                  className="form-input !py-2"
                >
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                </select>
              </div>

              <button
                onClick={() =>
                  setDeleteModal({ isOpen: true, contact: viewModal.contact })
                }
                className="btn btn-danger"
              >
                <FiTrash2 className="mr-2" size={16} />
                Delete
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, contact: null })}
        title="Delete Contact Message"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete this message from{" "}
            <span className="font-medium">{deleteModal.contact?.name}</span>?
            This action cannot be undone.
          </p>
          <div className="flex space-x-3 justify-end">
            <button
              onClick={() => setDeleteModal({ isOpen: false, contact: null })}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(deleteModal.contact._id)}
              className="btn btn-danger"
            >
              Delete Message
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Contacts;
